import { FilteringModel, FilterOption, SortingModel } from "@/services/filtering";
import { deepCopy } from "@/utils/objects";

/** Egy oszlopszűrő menthető értéke: szöveg → value, dátumtartomány → min/max, többválasztós → selectedOptions. */
export interface FilterValue {
  value?: unknown;
  min?: unknown;
  max?: unknown;
  selectedOptions?: unknown[];
}

function filterValue(item: unknown): FilterValue {
  const f = item as Record<string, unknown>;
  // A kijelölés sorrendje nem számít: rendezve, hogy az "összes" akkor is alapértéknek számítson, ha át lett kattintva.
  if ("selectedOptions" in f) return { selectedOptions: [...((f.selectedOptions as unknown[]) ?? [])].sort() };
  if ("min" in f) return { min: f.min || null, max: f.max || null };
  return { value: f.value || null };
}

function setFilterValue(item: unknown, value: FilterValue) {
  const f = item as Record<string, unknown>;
  if ("selectedOptions" in f) f.selectedOptions = [...(value.selectedOptions ?? [])];
  else if ("min" in f) {
    f.min = value.min ?? null;
    f.max = value.max ?? null;
  } else f.value = value.value ?? null;
}

export default class ApiSearch {
  currentPage = 1;
  pageSize = 10;
  searchText = "";
  filter: FilteringModel | undefined;
  extraFilter: any;
  private initFilter: FilteringModel | undefined;
  sort: SortingModel | undefined;
  private initSort: SortingModel | undefined;
  /** While it is true, the page doesn't load automacaly if filters(ApiSearch.Filter or ApiSearch.Sord) changed.  */
  stopFilterLoad = false;
  stopSortLoad = false;

  constructor(params?: Partial<ApiSearch>) {
    const data = { ...params };
    Object.assign(this, data);

    if (this.filter) this.initFilter = new FilteringModel(deepCopy(this.filter));
    if (this.sort) this.initSort = new SortingModel(deepCopy(this.sort));
  }

  /**
   * A szűrők értékei az induló állapotra, a szűrő-objektumok cseréje nélkül: egy JSON-másolatból épített
   * új modellről a szűrők metódusai (hasValue, clear) elvesznének.
   */
  resetFiltering() {
    if (!this.filter || !this.initFilter) return;
    const initFilters = this.initFilter.filters;
    Object.entries(this.filter.filters).forEach(([key, f]) => {
      if (initFilters[key]) setFilterValue(f, filterValue(initFilters[key]));
    });
  }

  /** Az induló szűrőtől eltérő oszlopszűrők értékei kulcs szerint (ezt menti az elrendezés-profil). */
  changedFilterValues(): Record<string, FilterValue> {
    const changed: Record<string, FilterValue> = {};
    if (!this.filter || !this.initFilter) return changed;
    const initFilters = this.initFilter.filters;
    Object.entries(this.filter.filters).forEach(([key, f]) => {
      const value = filterValue(f);
      if (initFilters[key] && JSON.stringify(value) !== JSON.stringify(filterValue(initFilters[key]))) changed[key] = value;
    });
    return changed;
  }

  /** Mentett értékek a szűrőkre; a nem szereplő kulcsok változatlanok maradnak. */
  applyFilterValues(values: Record<string, FilterValue>) {
    Object.entries(values).forEach(([key, value]) => {
      const f = this.filter?.filters[key];
      if (f) setFilterValue(f, value);
    });
  }

  resetSorting() {
    if (!this.initSort) return;
    this.sort = new SortingModel(deepCopy(this.initSort));
  }
}

export enum ColumnType {
  Money = "money",
  Date = "date",
  DateTime = "datetime",
  Center = "center",
  Right = "right",
}

export class ApiSearchColumn {
  field?: string | undefined = "";
  field2?: string | undefined = "";
  multiselectField?: string | undefined;
  filterable? = true;
  sortable? = true;
  title? = "";
  headerClass?: string;
  fieldClass?: string;
  type?: ColumnType;
  cell?: string;
  extraFilterValue? = "";
  multiselect? = false;
  multiselectOptions?: FilterOption[] | undefined = undefined;
  multiselectSelect?: number | undefined | Array<number>;
  header?: string;
  range? = false;
  hide?: false;
  isSecod? = false;
  /** A szűrősorban egy összes ki/be jelölő (AllCheckUncheck) a mezőre. */
  checkBox? = false;
  width?: number | undefined;
  visible?: boolean = true;
  income?: string;

  constructor(params?: Partial<ApiSearchColumn>) {
    const data = { ...params };
    Object.assign(this, data);

    if (typeof (params?.visible) === 'function') {
      this.visible = (params.visible as () => boolean)();
    } else {
      this.visible = params?.visible !== false;
    }
  }
}
